/*
 * Course-design upsell — "same design, more to wear".
 *
 * Two custom elements share one cart-add path:
 *   <course-upsell>  product page. Reads the LIVE design from <course-designer>,
 *                    bakes a per-garment proof, and adds the sibling garment with
 *                    the full design. Gated to enable only once a real design
 *                    exists; previews the placeholder until then.
 *   <cart-upsell>    cart drawer / cart page. Carries the design captured on an
 *                    existing line item (course, title, ink, map URLs) onto another
 *                    garment. No proof — the live design token only exists on the
 *                    product page — so the new line shows its plain garment photo;
 *                    the design data is still captured for fulfilment.
 */
(function () {
  'use strict';

  // The cart UI to refresh after an add: the drawer/notification on most pages,
  // or the cart-items section on the cart page.
  function cartElement() {
    // Prefer the full cart page when we're on it (update in place); otherwise the
    // drawer / notification used on every other page.
    return (
      document.querySelector('cart-items') ||
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification')
    );
  }

  // Shopify renders image_url as origin-relative (`/cdn/shop/…`) or protocol-
  // relative URLs, so in `shopify theme dev` (http://127.0.0.1) or on a custom
  // domain the raw value isn't a fetchable, allow-listed https URL — and the
  // proof bake 422s, silently dropping the design from the preview and the added
  // line. Mirror course-designer's garmentUrl(): resolve the path + query onto
  // the shop's permanent *.myshopify.com domain (always CDN-served and
  // allow-listed by the API), falling back to the resolved host without it.
  function garmentAbsUrl(url, shopDomain) {
    if (!url) return '';
    var abs;
    try {
      abs = new URL(url, window.location.href);
    } catch (e) {
      return '';
    }
    var pathq = abs.pathname + abs.search;
    if (shopDomain) return 'https://' + shopDomain + pathq;
    return 'https://' + abs.host + pathq;
  }

  // Garments are photographed at different framings, so each tile carries its
  // own map placement (snippets/garment-overlay-placement). Fall back to the
  // element-wide theme setting for tiles rendered without one.
  function placementFor(item, fallback) {
    var d = item.dataset;
    var top = parseFloat(d.overlayTopPct);
    var left = parseFloat(d.overlayLeftPct);
    var width = parseFloat(d.overlayWidthPct);
    return {
      top: isNaN(top) ? fallback.top : top,
      left: isNaN(left) ? fallback.left : left,
      width: isNaN(width) ? fallback.width : width,
    };
  }

  // A `position: fixed` element is positioned relative to the nearest ancestor
  // that establishes a containing block (a transform/filter/perspective), not the
  // viewport — and the cart drawer's panel does exactly that. Return that block's
  // viewport rect (or a zero origin on the cart page) so fixed coordinates derived
  // from getBoundingClientRect land in the right place in both contexts.
  function fixedOrigin(el) {
    var node = el.parentElement;
    while (node && node !== document.documentElement) {
      var s = getComputedStyle(node);
      if (
        (s.transform && s.transform !== 'none') ||
        (s.perspective && s.perspective !== 'none') ||
        (s.filter && s.filter !== 'none') ||
        (s.willChange && s.willChange.indexOf('transform') !== -1)
      ) {
        return node.getBoundingClientRect();
      }
      node = node.parentElement;
    }
    return { left: 0, top: 0 };
  }

  // The cart UI's sections to re-render, guarded — a cart component whose
  // getSectionsToRender touches an element absent on this page must never block an
  // add. Returns [] on any failure (the line is still added; we just full-reload).
  function sectionsToRender(cart) {
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
    try {
      return cart.getSectionsToRender() || [];
    } catch (e) {
      return [];
    }
  }

  // POST one line to the cart with design line-item properties, then repaint the
  // active cart UI. Mirrors product-form.js: requests the cart sections so the
  // drawer/page updates in place. Resolves with the cart response.
  function addDesignLine(variantId, quantity, properties) {
    var cart = cartElement();
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity || 1);
    Object.keys(properties || {}).forEach(function (key) {
      var value = properties[key];
      if (value != null && value !== '') formData.append('properties[' + key + ']', value);
    });

    var sections = sectionsToRender(cart);
    if (sections.length) {
      formData.append('sections', sections.map(function (s) { return s.section || s.id; }).join(','));
      formData.append('sections_url', window.location.pathname);
      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: formData,
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) throw response; // Shopify error payload
        repaintCart(cart, response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'course-upsell', cartData: response });
        }
        return response;
      });
  }

  function repaintCart(cart, response) {
    if (!cart) { window.location = window.routes.cart_url; return; }
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

    // Drawer / notification: renders its sections and opens itself.
    if (typeof cart.renderContents === 'function') { cart.renderContents(response); return; }

    // Cart page: replace each requested section's contents in place.
    var sections = sectionsToRender(cart);
    if (!sections.length || !response.sections) { window.location = window.routes.cart_url; return; }
    sections.forEach(function (section) {
      var el = document.getElementById(section.id);
      if (!el) return;
      var target = (section.selector ? el.querySelector(section.selector) : null) || el;
      var html = response.sections[section.section || section.id];
      if (!html) return;
      if (typeof cart.getSectionInnerHTML === 'function') {
        target.innerHTML = cart.getSectionInnerHTML(html, section.selector);
      } else {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector(section.selector || '.shopify-section');
        target.innerHTML = source ? source.innerHTML : html;
      }
    });
  }

  function setLoading(button, on) {
    if (!button) return;
    button.setAttribute('aria-disabled', on ? 'true' : 'false');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  }

  function selectedVariant(item) {
    var select = item.querySelector('.course-upsell__variant');
    if (select && select.value) return select.value;
    return item.dataset.variantId || '';
  }

  // ---- Product page: <course-upsell> -------------------------------------
  class CourseUpsell extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.designer = document.querySelector('course-designer');
      this.shopDomain =
        this.dataset.shopDomain || (this.designer && this.designer.dataset.shopDomain) || '';
      this.overlay = {
        top: parseFloat(this.dataset.overlayTopPct) || 0,
        left: parseFloat(this.dataset.overlayLeftPct) || 0,
        width: parseFloat(this.dataset.overlayWidthPct) || 50,
      };
      this.items = Array.prototype.slice.call(this.querySelectorAll('.course-upsell__item'));
      this.items.forEach(function (item) {
        var btn = item.querySelector('.course-upsell__add');
        if (btn) btn.addEventListener('click', this.onAdd.bind(this, item));
      }.bind(this));

      // Mirror the main product: repaint previews + gate buttons whenever the
      // design changes (generate / colour swap), and once on connect for the
      // current state (the designer may already be on the placeholder or a design).
      this._onChange = this.syncToDesign.bind(this);
      document.addEventListener('course-designer:design-changed', this._onChange);
      this.syncToDesign();
    }

    disconnectedCallback() {
      if (this._onChange) document.removeEventListener('course-designer:design-changed', this._onChange);
    }

    designerReady() {
      return this.designer && typeof this.designer.previewSrc === 'function';
    }

    syncToDesign() {
      var ready = this.designerReady();
      var hasDesign = ready ? this.designer.hasDesign() : false;
      this.items.forEach(function (item) {
        var ink = item.dataset.ink || 'green';
        var map = item.querySelector('[data-ref="upsell-map"]');
        if (map && ready) {
          var src = this.designer.previewSrc(ink);
          if (src) map.src = src;
          // Generated maps are on white → multiply onto the garment; the
          // transparent placeholder blends normally.
          map.style.mixBlendMode = hasDesign ? 'multiply' : 'normal';
        }
        var btn = item.querySelector('.course-upsell__add');
        if (btn) btn.disabled = !hasDesign;
      }.bind(this));
    }

    onAdd(item, evt) {
      if (evt) evt.preventDefault();
      if (!this.designerReady() || !this.designer.hasDesign()) return;
      var variantId = selectedVariant(item);
      if (!variantId) return;

      var btn = item.querySelector('.course-upsell__add');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return; // add in flight
      var ink = item.dataset.ink || 'green';
      var data = this.designer.designData();
      var place = placementFor(item, this.overlay);
      setLoading(btn, true);

      this.designer
        .bakeProofForGarment({
          garmentUrl: garmentAbsUrl(item.dataset.garmentUrl, this.shopDomain),
          color: ink,
          overlayTopPct: place.top,
          overlayLeftPct: place.left,
          overlayWidthPct: place.width,
        })
        .catch(function () { return null; }) // proof is best-effort; never block the sale
        .then(function (proofUrl) {
          return addDesignLine(variantId, 1, {
            Course: data.course,
            Title: data.title,
            '_Course ID': data.courseId,
            '_Title font': data.titleFont,
            '_Course ink': ink,
            '_Map green': data.mapGreen,
            '_Map cream': data.mapCream,
            '_Title geom': data.geom,
            '_Course data': data.completeness,
            '_Proof image': proofUrl || '',
          });
        })
        .catch(function () {})
        .then(function () { setLoading(btn, false); });
    }
  }

  // ---- Cart: <cart-upsell> ------------------------------------------------
  // No live design token exists in the cart, so each offered garment is baked
  // token-free from the captured map + title geometry (/api/course-proof-from-map)
  // and swapped in as the preview. The same proof rides onto the line when added.
  //
  // Layout is a horizontal scroll row of compact tiles. A tile's `+` reveals the
  // sizes as a vertical list that pops up above the garment photo: hovering (or
  // focusing) the `+` opens it, and this element positions that popup `fixed` so
  // it escapes the scroller's clipping. Also handles touch toggling, single-variant
  // direct adds, and the size-chip adds.
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.shopDomain = this.dataset.shopDomain || '';
      this.overlay = {
        top: parseFloat(this.dataset.overlayTopPct) || 0,
        left: parseFloat(this.dataset.overlayLeftPct) || 0,
        width: parseFloat(this.dataset.overlayWidthPct) || 50,
      };
      this.openItem = null; // the tile whose size popup is currently shown
      this.pinnedItem = null; // a tile tapped open (stays until dismissed)
      this._closeTimer = null;

      this.querySelectorAll('.course-upsell__item').forEach(function (item) {
        var plus = item.querySelector('.course-upsell__plus');
        var menu = item.querySelector('[data-ref="addmenu"]');
        if (plus) plus.addEventListener('click', this.onPlus.bind(this, item, plus));

        // Reveal the sizes while the pointer/focus is on the add menu (the `+`
        // and the popup above it). A short close delay bridges the gap between
        // the `+` and the floating popup. Single-variant tiles add from the `+`.
        if (menu && plus && !plus.dataset.single) {
          menu.addEventListener('mouseenter', this.onMenuEnter.bind(this, item));
          menu.addEventListener('mouseleave', this.scheduleClose.bind(this, item));
          menu.addEventListener('focusin', this.onMenuEnter.bind(this, item));
          menu.addEventListener('focusout', this.onMenuFocusOut.bind(this, item, menu));
        }

        item.querySelectorAll('.course-upsell__size').forEach(function (chip) {
          if (chip.hasAttribute('disabled')) return;
          chip.addEventListener('click', this.onAdd.bind(this, item, chip.dataset.variantId));
        }.bind(this));
        this.bakePreview(item);
      }.bind(this));

      // Keep an open popup glued to its `+` while the cart scrolls or resizes.
      this._onReflow = function () { if (this.openItem) this.positionMenu(this.openItem); }.bind(this);
      window.addEventListener('resize', this._onReflow);
      window.addEventListener('scroll', this._onReflow, true);

      // Close a tapped-open popup when tapping away or pressing Escape.
      this._onDocClick = this.closeOpen.bind(this);
      this._onKey = function (e) { if (e.key === 'Escape') this.closeOpen(); }.bind(this);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._onReflow) {
        window.removeEventListener('resize', this._onReflow);
        window.removeEventListener('scroll', this._onReflow, true);
      }
    }

    onMenuEnter(item) {
      if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
      this.openMenu(item);
    }

    scheduleClose(item) {
      if (this.pinnedItem === item) return; // tapped open — stays until dismissed
      if (this._closeTimer) clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(function () {
        this._closeTimer = null;
        this.closeMenu(item);
      }.bind(this), 120);
    }

    onMenuFocusOut(item, menu, evt) {
      if (menu.contains(evt.relatedTarget)) return; // focus moved within the menu
      if (this.pinnedItem === item) return;
      this.closeMenu(item);
    }

    // Reveal the size popup for `item`, positioned above its `+`.
    openMenu(item) {
      var menu = item.querySelector('[data-ref="addmenu"]');
      var pop = item.querySelector('.course-upsell__quickadd');
      if (!menu || !pop) return; // single-variant tiles have no popup
      if (this.openItem && this.openItem !== item) this.closeMenu(this.openItem);
      this.openItem = item;
      this.positionMenu(item); // place it before it becomes visible (no flash)
      menu.classList.add('is-open');
      var plus = item.querySelector('.course-upsell__plus');
      if (plus) plus.setAttribute('aria-expanded', 'true');
    }

    closeMenu(item) {
      var menu = item && item.querySelector('[data-ref="addmenu"]');
      if (menu) menu.classList.remove('is-open');
      var plus = item && item.querySelector('.course-upsell__plus');
      if (plus && !plus.dataset.single) plus.setAttribute('aria-expanded', 'false');
      if (this.pinnedItem === item) this.pinnedItem = null;
      if (this.openItem === item) this.openItem = null;
    }

    // Close whatever popup is open (document-click / Escape).
    closeOpen() {
      if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
      if (this.openItem) this.closeMenu(this.openItem);
    }

    // Anchor the fixed popup to the `+`: right-aligned, rising just above it.
    // Coordinates are taken relative to the popup's containing block so it lands
    // correctly inside the transformed cart drawer as well as on the cart page.
    positionMenu(item) {
      var plus = item.querySelector('.course-upsell__plus');
      var pop = item.querySelector('.course-upsell__quickadd');
      if (!plus || !pop) return;
      var r = plus.getBoundingClientRect();
      var pr = pop.getBoundingClientRect();
      var origin = fixedOrigin(pop);
      var left = Math.min(r.right - pr.width, window.innerWidth - pr.width - 8);
      left = Math.max(8, left);
      var top = Math.max(8, r.top - pr.height - 6);
      pop.style.left = left - origin.left + 'px';
      pop.style.top = top - origin.top + 'px';
    }

    // The `+` button: single-variant garments add straight away; multi-variant
    // ones toggle (pin) the size popup so touch/keyboard users get what hover gives.
    onPlus(item, plus, evt) {
      if (evt) { evt.preventDefault(); evt.stopPropagation(); }
      if (plus.getAttribute('aria-disabled') === 'true') return; // add in flight
      if (plus.dataset.single) {
        this.onAdd(item, plus.dataset.variantId);
        return;
      }
      if (this.pinnedItem === item) {
        this.closeMenu(item);
      } else {
        this.openMenu(item);
        this.pinnedItem = item;
      }
    }

    // Parse the stored "left,right,cy,h,canvasW,canvasH[,align[,bandH]]" geometry
    // into the proof request fields. The two trailing fields are optional: line
    // items added before they existed carry six numbers, and are re-baked with
    // the right-aligned, width-only fit they were drawn with. Returns null when
    // the geometry is absent entirely (older line items) so we skip baking.
    geomFields(item) {
      var raw = (item.dataset.geom || '').split(',');
      var parts = raw.map(parseFloat);
      if (parts.length < 6 || parts.slice(0, 6).some(isNaN)) return null;
      var out = {
        title_left_mm: parts[0], title_right_mm: parts[1],
        title_cy_mm: parts[2], title_h_mm: parts[3],
        canvas_w_mm: parts[4], canvas_h_mm: parts[5],
        title_align: (raw[6] || '').trim() === 'start' ? 'start' : 'end',
      };
      if (parts.length > 7 && !isNaN(parts[7]) && parts[7] > 0) {
        out.title_band_h_mm = parts[7];
      }
      return out;
    }

    // Bake a real proof of this design on this garment and swap it in. Best-effort:
    // on any failure the plain garment photo + map overlay stays as the preview.
    // The in-flight promise is kept on the item so an add clicked mid-bake can
    // still wait for (and carry) the proof rather than posting an empty one.
    bakePreview(item) {
      if (!this.apiBase) return;
      var map = item.dataset.map;
      var garment = garmentAbsUrl(item.dataset.garmentUrl, this.shopDomain);
      var geom = this.geomFields(item);
      if (!map || !garment || !geom) return;

      var place = placementFor(item, this.overlay);
      var body = Object.assign({
        map_url: map,
        title: item.dataset.title || item.dataset.course || '',
        title_font: item.dataset.titleFont || 'birthstone',
        color: item.dataset.ink || 'green',
        garment_url: garment,
        overlay_top_pct: place.top,
        overlay_left_pct: place.left,
        overlay_width_pct: place.width,
      }, geom);

      item._bakePromise = fetch(this.apiBase + '/api/course-proof-from-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          if (!data || !data.url) return;
          item.dataset.proof = data.url;
          // The proof already includes the garment, so show it whole and drop the
          // separate map overlay.
          var img = item.querySelector('[data-ref="upsell-garment"]');
          if (img) img.src = data.url;
          var overlay = item.querySelector('[data-ref="upsell-overlay"]');
          if (overlay) overlay.remove();
        })
        .catch(function () { /* keep the photo + overlay fallback */ });
    }

    onAdd(item, variantId, evt) {
      if (evt) { evt.preventDefault(); evt.stopPropagation(); }
      if (!item || !variantId) return;
      if (item.classList.contains('course-upsell__item--loading')) return;

      var plus = item.querySelector('.course-upsell__plus');
      var p = item.dataset;
      this.setTileLoading(item, plus, true);
      // If the proof bake is still in flight, wait for it so the added line
      // carries the baked design instead of a blank garment. Bake failures
      // resolve (the promise never rejects), so this can't block the add.
      Promise.resolve(item._bakePromise)
        .then(function () {
          return addDesignLine(variantId, 1, {
            Course: p.course || '',
            Title: p.title || '',
            '_Course ID': p.courseId || '',
            '_Title font': p.titleFont || 'birthstone',
            '_Course ink': p.ink || 'green',
            '_Map green': p.mapGreen || '',
            '_Map cream': p.mapCream || '',
            '_Title geom': p.geom || '',
            '_Proof image': p.proof || '',
          });
        })
        .catch(function () {})
        .then(function () { this.setTileLoading(item, plus, false); }.bind(this));
    }

    setTileLoading(item, plus, on) {
      item.classList.toggle('course-upsell__item--loading', on);
      if (on) this.closeMenu(item); // fold the size popup away while adding
      setLoading(plus, on); // toggles aria-disabled + the `+` spinner
    }
  }

  if (!customElements.get('course-upsell')) customElements.define('course-upsell', CourseUpsell);
  if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
})();
